import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { BlockingProgress } from '../components/BlockingProgress';
import { Lightbox } from '../components/Lightbox';
import { NotificationSetup } from '../components/NotificationSetup';
import { FeedCards } from '../components/FeedCards';
import { FamilyStoryTray } from '../components/FamilyStoryTray';
import { PhotosIcon } from '../components/Icons';
import { api, thumbUrl, type Album, type FeedResponse, type Group, type Photo } from '../lib/api';
import { readCachedAlbums, writeCachedAlbumCover, writeCachedAlbumList } from '../lib/albumCache';
import { readCachedFeed, writeCachedFeed } from '../lib/feedCache';

/** Everything shared with you, newest first. The app's home screen. */
export function FeedPage() {
  const location = useLocation();
  const { user } = useAuth();
  const [view, setView] = useState<'family' | 'mine'>('family');
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [albums, setAlbums] = useState<Album[]>([]);
  const [albumCovers, setAlbumCovers] = useState<Record<string, string | null>>({});
  const [groups, setGroups] = useState<Group[]>([]);
  const [groupsLoading, setGroupsLoading] = useState(true);
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [moments, setMoments] = useState<Array<{ kind: 'on_this_day' | 'rediscover'; title: string; detail: string; photos: Photo[] }>>([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<number | null>(null);
  const [deepLinkedPost, setDeepLinkedPost] = useState<Photo | null>(null);
  const [deepLinkLoading, setDeepLinkLoading] = useState(false);
  const [deepLinkError, setDeepLinkError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [uploadNote, setUploadNote] = useState<string | null>(null);
  const [caption, setCaption] = useState('');
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [pendingNewPosts, setPendingNewPosts] = useState<Photo[]>([]);
  const [feedCacheStatus, setFeedCacheStatus] = useState<'idle' | 'refreshing' | 'saved'>('idle');

  const fileInput = useRef<HTMLInputElement>(null);
  const loadMoreTarget = useRef<HTMLDivElement>(null);
  const loadingMoreRef = useRef(false);
  const feedRequestVersion = useRef(0);
  const loadMoreRequestVersion = useRef(0);
  const photosRef = useRef<Photo[]>([]);
  const feedReadyRef = useRef(false);
  const checkingForPostsRef = useRef(false);

  const applyFeedResponse = useCallback((result: FeedResponse) => {
    photosRef.current = result.items;
    setPhotos(result.items);
    setPendingNewPosts([]);
    setNextCursor(result.nextCursor);
    setMoments(result.moments.filter((moment) => moment.kind !== 'rediscover' || !sessionStorage.getItem('family.rediscover.seen')));
    feedReadyRef.current = true;
  }, []);

  const load = useCallback(async () => {
    const requestVersion = ++feedRequestVersion.current;
    ++loadMoreRequestVersion.current;
    loadingMoreRef.current = false;
    setLoadingMore(false);
    feedReadyRef.current = false;
    const hasSavedPosts = photosRef.current.length > 0;
    setLoading(!hasSavedPosts);
    if (hasSavedPosts) setFeedCacheStatus('refreshing');
    try {
      const result = await api.feed(undefined, view);
      if (requestVersion !== feedRequestVersion.current) return;
      applyFeedResponse(result);
      setError(null);
      setFeedCacheStatus('idle');
      if (user) void writeCachedFeed(user.id, view, result);
    } catch (caught) {
      if (requestVersion !== feedRequestVersion.current) return;
      if (photosRef.current.length > 0) {
        setError(null);
        setFeedCacheStatus('saved');
        feedReadyRef.current = true;
      } else {
        setError(caught instanceof Error ? caught.message : 'Could not load photos.');
        setFeedCacheStatus('idle');
      }
    } finally {
      if (requestVersion === feedRequestVersion.current) {
        setLoading(false);
      }
    }
  }, [applyFeedResponse, user, view]);

  useEffect(() => {
    let current = true;
    const hydrationVersion = ++feedRequestVersion.current;
    ++loadMoreRequestVersion.current;
    loadingMoreRef.current = false;
    feedReadyRef.current = false;
    photosRef.current = [];
    setPhotos([]);
    setMoments([]);
    setNextCursor(null);
    setPendingNewPosts([]);
    setLoadingMore(false);
    setLoading(true);
    setError(null);
    setFeedCacheStatus('idle');

    void (async () => {
      const cached = user ? await readCachedFeed(user.id, view) : null;
      if (!current || hydrationVersion !== feedRequestVersion.current) return;
      if (cached) {
        applyFeedResponse(cached.response);
        setLoading(false);
        setFeedCacheStatus('refreshing');
      }
      if (current) await load();
    })();

    return () => {
      current = false;
    };
  }, [applyFeedResponse, load, user, view]);

  /** Notification links open a focused post without reordering the feed. */
  useEffect(() => {
    const postId = new URLSearchParams(location.search).get('post');
    if (!postId) {
      setDeepLinkedPost(null);
      setDeepLinkError(null);
      setDeepLinkLoading(false);
      return;
    }

    let current = true;
    setSelected(null);
    setDeepLinkedPost(null);
    setDeepLinkError(null);
    setDeepLinkLoading(true);
    void api.feedPost(postId).then(({ post }) => {
      if (current) setDeepLinkedPost(post);
    }).catch(() => {
      if (current) setDeepLinkError('This post is no longer available to your account.');
    }).finally(() => {
      if (current) setDeepLinkLoading(false);
    });

    return () => {
      current = false;
    };
  }, [location.search]);

  const clearPostLink = useCallback(() => {
    setDeepLinkedPost(null);
    setDeepLinkError(null);
    const url = new URL(window.location.href);
    url.searchParams.delete('post');
    url.hash = '';
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}`);
  }, []);

  useEffect(() => {
    let current = true;
    void (async () => {
      const cached = user ? await readCachedAlbums(user.id) : null;
      if (!current) return;
      if (cached) {
        setAlbums(cached.albums);
        setAlbumCovers(cached.covers);
      }
      try {
        const result = await api.albums();
        if (!current) return;
        setAlbums(result.albums);
        if (user) void writeCachedAlbumList(user.id, result.albums);
      } catch {
        if (!cached && current) setAlbums([]);
      }
    })();
    return () => {
      current = false;
    };
  }, [user]);

  const resolveAlbumCover = useCallback((album: Album, fileId: string | null) => {
    setAlbumCovers((current) => ({ ...current, [album.path]: fileId }));
    if (user) void writeCachedAlbumCover(user.id, album, fileId);
  }, [user]);

  useEffect(() => {
    void api.groups().then((result) => {
      setGroups(result.groups);
      const available = new Set(result.groups.map((group) => group.id));
      // Sharing is intentionally opt-in. Preselecting every circle makes one
      // missed glance an audience leak.
      setSelectedGroups((current) => current.filter((groupId) => available.has(groupId)));
    }).catch(() => setGroups([])).finally(() => setGroupsLoading(false));
  }, []);

  useEffect(() => {
    const openPicker = () => setShowUpload(true);
    window.addEventListener('family:open-upload', openPicker);
    return () => window.removeEventListener('family:open-upload', openPicker);
  }, []);

  const submitPost = async () => {
    if (pendingFiles.length === 0 || selectedGroups.length === 0 || uploadProgress !== null) return;

    setUploadProgress(0);
    setUploadNote(null);
    setShowUpload(false);

    try {
      const result = await api.upload(pendingFiles, selectedGroups, caption, (fraction) =>
        setUploadProgress(fraction),
      );

      const parts: string[] = [];
      const names = new Set(result.uploaded.map((file) => file.name));
      if (names.size > 0) {
        setCaption('');
        setPendingFiles([]);
        parts.push(
          `Shared ${names.size} ${names.size === 1 ? 'photo' : 'photos'} with ${selectedGroups.length} ${selectedGroups.length === 1 ? 'group' : 'groups'}.`,
        );
      }
      if (result.rejected.length > 0) {
        parts.push(`${result.rejected.length} skipped (${result.rejected[0]?.reason}).`);
        if (names.size === 0) setShowUpload(true);
      }
      setUploadNote(parts.join(' ') || 'Nothing to upload.');
      if (view === 'mine') await load();
      else {
        setLoading(true);
        setView('mine');
      }
    } catch (caught) {
      setUploadNote(caught instanceof Error ? caught.message : 'Upload failed.');
      setShowUpload(true);
    } finally {
      setUploadProgress(null);
    }
  };

  const loadMore = useCallback(async () => {
    if (!nextCursor || loadingMoreRef.current) return;
    const feedVersion = feedRequestVersion.current;
    const requestVersion = ++loadMoreRequestVersion.current;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const result = await api.feed(nextCursor, view);
      if (feedVersion !== feedRequestVersion.current || requestVersion !== loadMoreRequestVersion.current) return;
      setPhotos((current) => {
        const visible = new Set(current.map((photo) => photo.fileId));
        const next = [...current, ...result.items.filter((photo) => !visible.has(photo.fileId))];
        photosRef.current = next;
        return next;
      });
      setNextCursor(result.nextCursor);
    } catch (caught) {
      if (feedVersion !== feedRequestVersion.current || requestVersion !== loadMoreRequestVersion.current) return;
      setError(caught instanceof Error ? caught.message : 'Could not load more photos.');
    } finally {
      if (requestVersion === loadMoreRequestVersion.current) {
        loadingMoreRef.current = false;
        setLoadingMore(false);
      }
    }
  }, [nextCursor, view]);

  /** Load the next small page before the family reaches the end of this one. */
  useEffect(() => {
    const target = loadMoreTarget.current;
    if (!target || !nextCursor || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) void loadMore();
    }, { rootMargin: '600px 0px' });
    observer.observe(target);
    return () => observer.disconnect();
  }, [loadMore, nextCursor]);

  const checkForNewPosts = useCallback(async () => {
    if (
      checkingForPostsRef.current
      || !feedReadyRef.current
      || document.visibilityState !== 'visible'
      || !navigator.onLine
    ) return;
    checkingForPostsRef.current = true;
    const feedVersion = feedRequestVersion.current;
    try {
      const result = await api.feed(undefined, view);
      if (feedVersion !== feedRequestVersion.current) return;
      const known = new Set(photosRef.current.map((photo) => photo.postId ?? photo.fileId));
      const fresh = result.items.filter((photo) => !known.has(photo.postId ?? photo.fileId));
      if (fresh.length > 0) {
        setPendingNewPosts((current) => {
          const queued = new Set(current.map((photo) => photo.postId ?? photo.fileId));
          return [...fresh.filter((photo) => !queued.has(photo.postId ?? photo.fileId)), ...current]
            .sort((a, b) => b.lastModified - a.lastModified);
        });
      }
    } catch {
      // The normal feed remains usable; the next visible check retries.
    } finally {
      checkingForPostsRef.current = false;
    }
  }, [view]);

  /** Near-live without a permanent socket: no work while hidden or offline. */
  useEffect(() => {
    const interval = window.setInterval(() => void checkForNewPosts(), 20_000);
    const onVisible = () => {
      if (document.visibilityState === 'visible') void checkForNewPosts();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [checkForNewPosts]);

  const revealNewPosts = () => {
    if (pendingNewPosts.length === 0) return;
    const scroller = document.querySelector<HTMLElement>('.app-main');
    const previousTop = scroller?.scrollTop ?? 0;
    const previousHeight = scroller?.scrollHeight ?? 0;
    const keepCurrentCard = previousTop > 80;
    const queued = pendingNewPosts;
    setPhotos((current) => {
      const ids = new Set(queued.map((photo) => photo.postId ?? photo.fileId));
      const next = [...queued, ...current.filter((photo) => !ids.has(photo.postId ?? photo.fileId))]
        .sort((a, b) => b.lastModified - a.lastModified);
      photosRef.current = next;
      return next;
    });
    setPendingNewPosts([]);
    window.requestAnimationFrame(() => {
      if (!scroller) return;
      scroller.scrollTop = keepCurrentCard
        ? previousTop + (scroller.scrollHeight - previousHeight)
        : 0;
    });
  };

  const updatePhoto = (fileId: string, update: Pick<Photo, 'likeCount' | 'likedByMe'>) => {
    setPhotos((current) => {
      const next = current.map((photo) => photo.fileId === fileId ? { ...photo, ...update } : photo);
      photosRef.current = next;
      if (user) void writeCachedFeed(user.id, view, { items: next, nextCursor, moments });
      return next;
    });
  };

  const closeUpload = () => {
    setShowUpload(false);
    setCaption('');
    setPendingFiles([]);
    window.requestAnimationFrame(() => document.querySelector<HTMLButtonElement>('.upload-nav')?.focus());
  };

  return (
    <>
      <header className="app-header feed-header">
        <div>
          <h1 className="wordmark">Family</h1>
          <span className="feed-header-note">For your people</span>
        </div>
        <div className="feed-view-switch" role="tablist" aria-label="Post view">
          <button type="button" role="tab" aria-selected={view === 'family'} className={view === 'family' ? 'active' : ''} onClick={() => { setLoading(true); setView('family'); }}>Family</button>
          <button type="button" role="tab" aria-selected={view === 'mine'} className={view === 'mine' ? 'active' : ''} onClick={() => { setLoading(true); setView('mine'); }}>Your posts</button>
        </div>
      </header>

      {pendingNewPosts.length > 0 && (
        <button type="button" className="new-posts-banner" onClick={revealNewPosts} aria-live="polite">
          <span aria-hidden="true">↑</span>
          Show {pendingNewPosts.length} new {pendingNewPosts.length === 1 ? 'post' : 'posts'}
        </button>
      )}

      <div className="page feed-page">
        {feedCacheStatus === 'refreshing' && (
          <div className="feed-refresh-progress" role="status" aria-label="Refreshing family posts">
            <span />
          </div>
        )}
        <input
          ref={fileInput}
          hidden
          type="file"
          // `multiple` plus these types gives iOS the photo library picker
          // rather than a plain file browser.
          accept="image/*,video/*"
          multiple
          aria-hidden="true"
          tabIndex={-1}
          onChange={(event) => {
            const chosen = Array.from(event.target.files ?? []);
            if (chosen.length > 0) setPendingFiles(chosen);
            event.currentTarget.value = '';
          }}
        />

        {showUpload && (
          <UploadSheet
            groups={groups}
            loading={groupsLoading}
            selectedGroups={selectedGroups}
            files={pendingFiles}
            caption={caption}
            onChange={setSelectedGroups}
            onCaptionChange={setCaption}
            onChoose={() => fileInput.current?.click()}
            onPost={() => void submitPost()}
            onRemoveFile={(index) => setPendingFiles((current) => current.filter((_, currentIndex) => currentIndex !== index))}
            onClose={closeUpload}
          />
        )}

        {uploadNote && (
          <div className="banner">
            <p>{uploadNote}</p>
          </div>
        )}

        {deepLinkError && (
          <div className="banner error">
            <h3>Post unavailable</h3>
            <p>{deepLinkError}</p>
            <button type="button" className="btn btn-sm" style={{ marginTop: 10 }} onClick={clearPostLink}>Back to the newest posts</button>
          </div>
        )}

        <NotificationSetup compact />

        <FamilyStoryTray />

        <AlbumStories albums={albums} covers={albumCovers} onCoverResolved={resolveAlbumCover} />

        {error && (
          <div className="banner error">
            <h3>Could not load photos</h3>
            <p>{error}</p>
            <button
              type="button"
              className="btn btn-sm"
              style={{ marginTop: 10 }}
              onClick={() => void load()}
            >
              Try again
            </button>
          </div>
        )}

        {feedCacheStatus === 'saved' && (
          <div className="feed-cache-notice" role="status">
            <div>
              <strong>Showing saved posts</strong>
              <span>The family cloud could not refresh. You can keep browsing these recent posts.</span>
            </div>
            <button type="button" onClick={() => void load()}>Try again</button>
          </div>
        )}

        {loading ? (
          <div className="center-fill">
            <div className="spinner" role="status" aria-label="Loading photos" />
          </div>
        ) : photos.length === 0 && !error ? (
          <div className="empty-state">
            <PhotosIcon />
            <h2>{view === 'mine' ? 'No posts from you yet' : 'Nothing new from family'}</h2>
            <p>{view === 'mine'
              ? 'Photos you share will be collected here.'
              : 'Your own posts stay out of this feed, so it’s easier to catch up with everyone else.'}</p>
          </div>
        ) : (
          <>
            <FeedMoments moments={moments} onSelect={(photo) => {
              if (photo.fileId && moments.some((moment) => moment.kind === 'rediscover')) sessionStorage.setItem('family.rediscover.seen', '1');
              setPhotos((current) => [photo, ...current.filter((item) => item.fileId !== photo.fileId)]);
              setSelected(0);
            }} />
            <FeedCards photos={photos} onSelect={(_photo, index) => setSelected(index)} onUpdate={updatePhoto} />
            {nextCursor && <div ref={loadMoreTarget} className="feed-load-sentinel" aria-live="polite">
              {loadingMore ? <><div className="spinner" aria-hidden="true" /><span>Bringing in more family posts…</span></> : <button type="button" onClick={() => void loadMore()}>Load more posts</button>}
            </div>}
          </>
        )}
      </div>

      {uploadProgress !== null && <BlockingProgress
        title={uploadProgress < 1 ? `Preparing & uploading ${Math.round(uploadProgress * 100)}%` : 'Finishing your post'}
        detail={uploadProgress < 1 ? 'Optimizing videos, then sending your media to the private family cloud.' : 'Saving your caption and notifying the selected family groups…'}
      />}

      {deepLinkLoading && <BlockingProgress title="Opening family post" detail="Checking the post with Nextcloud…" />}

      {deepLinkedPost && (
        <Lightbox
          photos={[deepLinkedPost]}
          index={0}
          onClose={clearPostLink}
          onIndexChange={() => undefined}
        />
      )}

      {!deepLinkedPost && selected !== null && (
        <Lightbox
          photos={photos}
          index={selected}
          onClose={() => setSelected(null)}
          onIndexChange={setSelected}
        />
      )}
    </>
  );
}

function FeedMoments({ moments, onSelect }: { moments: Array<{ kind: 'on_this_day' | 'rediscover'; title: string; detail: string; photos: Photo[] }>; onSelect: (photo: Photo) => void }) {
  if (!moments.length) return null;
  return <div className="feed-moments">{moments.map((moment) => <section className={`feed-moment ${moment.kind}`} key={moment.kind}>
    <div><p>{moment.title}</p><span>{moment.detail}</span></div>
    <div className="moment-photos">{moment.photos.map((photo) => <button type="button" key={photo.fileId} onClick={() => onSelect(photo)}><img src={thumbUrl(photo.fileId, 300)} alt={photo.name} loading="lazy" /></button>)}</div>
  </section>)}</div>;
}

function UploadSheet({ groups, loading, selectedGroups, files, caption, onChange, onCaptionChange, onChoose, onPost, onRemoveFile, onClose }: {
  groups: Group[];
  loading: boolean;
  selectedGroups: string[];
  files: File[];
  caption: string;
  onChange: (groups: string[]) => void;
  onCaptionChange: (caption: string) => void;
  onChoose: () => void;
  onPost: () => void;
  onRemoveFile: (index: number) => void;
  onClose: () => void;
}) {
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    closeRef.current?.focus();
  }, []);

  const toggle = (groupId: string) => onChange(selectedGroups.includes(groupId)
    ? selectedGroups.filter((id) => id !== groupId)
    : [...selectedGroups, groupId]);

  return (
    <div className="upload-sheet-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className="upload-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="share-title"
        onMouseDown={(event) => event.stopPropagation()}
        onKeyDown={(event) => {
          if (event.key === 'Escape') onClose();
        }}
      >
        <div className="upload-sheet-head"><div><p>New post</p><h2 id="share-title">Share with your people</h2></div><button ref={closeRef} type="button" onClick={onClose}>Close</button></div>
        {loading ? <div className="upload-groups-loading" role="status"><div className="spinner" aria-hidden="true" /><span>Loading your sharing groups…</span></div> : groups.length === 0 ? <p className="upload-empty">You are not in a sharing group yet. Ask a family admin to add you.</p> : <>
          <p className="upload-intro">Choose who should receive a private copy. You will review everything before it is posted.</p>
          {files.length > 0 && <SelectedMedia files={files} onRemove={onRemoveFile} />}
          {files.length > 0 && <div className="upload-caption-field">
            <label htmlFor="share-caption">Caption <span>Optional</span></label>
            <textarea
              id="share-caption"
              rows={3}
              maxLength={500}
              value={caption}
              placeholder="Add a note for the family…"
              onChange={(event) => onCaptionChange(event.target.value)}
            />
            <small>{caption.length}/500</small>
          </div>}
          <div className="group-picker">{groups.map((group) => <label key={group.id}><input type="checkbox" checked={selectedGroups.includes(group.id)} onChange={() => toggle(group.id)} /><span>{group.label}</span></label>)}</div>
          {files.length === 0 ? (
            <button type="button" className="btn btn-primary btn-block" onClick={onChoose} disabled={selectedGroups.length === 0}>Choose photos</button>
          ) : <div className="upload-review-actions">
            <button type="button" className="btn btn-secondary" onClick={onChoose}>Choose different photos</button>
            <button type="button" className="btn btn-primary" onClick={onPost} disabled={selectedGroups.length === 0}>Share post</button>
          </div>}
        </>}
      </section>
    </div>
  );
}

function SelectedMedia({ files, onRemove }: { files: File[]; onRemove: (index: number) => void }) {
  const [previews, setPreviews] = useState<string[]>([]);

  useEffect(() => {
    const urls = files.map((file) => URL.createObjectURL(file));
    setPreviews(urls);
    return () => urls.forEach((url) => URL.revokeObjectURL(url));
  }, [files]);

  return <section className="upload-media-review" aria-label={`${files.length} selected ${files.length === 1 ? 'photo' : 'photos'}`}>
    <div className="upload-media-summary"><strong>{files.length} selected</strong><span>Add a caption, then share when you’re ready.</span></div>
    <div className="upload-media-strip">{files.map((file, index) => <div className="upload-media-item" key={`${file.name}:${file.lastModified}:${index}`}>
      {file.type.startsWith('video/')
        ? <video src={previews[index]} muted playsInline preload="metadata" aria-label={file.name} />
        : <img src={previews[index]} alt={file.name} />}
      <button type="button" onClick={() => onRemove(index)} aria-label={`Remove ${file.name}`}>×</button>
    </div>)}</div>
  </section>;
}

function AlbumStories({ albums, covers, onCoverResolved }: {
  albums: Album[];
  covers: Record<string, string | null>;
  onCoverResolved: (album: Album, fileId: string | null) => void;
}) {
  if (albums.length === 0) return null;

  return (
    <section className="album-stories" aria-label="Albums">
      <div className="section-label">
        <span>Albums</span>
        <Link to="/albums">See all</Link>
      </div>
      <div className="story-row">
        {albums.map((album) => (
          <Link className="album-story" key={album.name} to={`/album/${encodeURIComponent(album.name)}`}>
            <LazyStoryCover album={album} cover={covers[album.path]} onResolved={onCoverResolved} />
            <span>{album.name}</span>
          </Link>
        ))}
      </div>
    </section>
  );
}

function LazyStoryCover({ album, cover, onResolved }: {
  album: Album;
  cover: string | null | undefined;
  onResolved: (album: Album, fileId: string | null) => void;
}) {
  const target = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (cover !== undefined) return;
    const element = target.current;
    if (!element) return;
    let current = true;
    const loadCover = () => {
      void api.albumPhotos(album.path).then((result) => {
        if (current) onResolved(album, result.photos[0]?.fileId ?? null);
      }).catch(() => undefined);
    };
    if (typeof IntersectionObserver === 'undefined') {
      loadCover();
      return () => { current = false; };
    }
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      observer.disconnect();
      loadCover();
    }, { rootMargin: '160px' });
    observer.observe(element);
    return () => {
      current = false;
      observer.disconnect();
    };
  }, [album, cover, onResolved]);

  return <span ref={target} className="album-story-cover">
    {cover ? <img src={thumbUrl(cover, 240)} alt="" loading="lazy" /> : <span>{album.name.slice(0, 1).toUpperCase()}</span>}
  </span>;
}
